import { DrawingPoint } from '../pages/catalog/sale/map-drawing.service';

export interface LoginFromData {
  email: string;
  password: string;
  cfTurnstileToken?: string;
}

export interface UserLoginResponse {
  result: {
    isAuthSuccessful: boolean;
    token: string;
    is2StepVerificationRequired: boolean;
    isEmailVerificationRequired: boolean;
    provider: string;
    passwordExpired: boolean;
  };
  successful: boolean;
  message: string;
}

export interface UserDetailsResponse extends BaseInterface {
  result: UserDetails;
}

export interface BaseInterface {
  result: Record<string, any>;
  successful: boolean;
  message: string;
  success?: boolean;
}

export interface ExtendedBaseInterface extends BaseInterface {
  pageNumber?: number;
  pageSize?: number;
  totalPages?: number;
  totalRecords?: number;
  status?: string;
  data?: any;
  items?: any;
  total?: number;
  count?: number;
  pages?: number;
}

export interface DocumentUploadResponse {
  blobUrl: string;
  contentType: string;
  fileName: string;
  id: number;
  order: number;
  sizeBytes: number;
}

export enum Roles {
  Agent = 'Agent',
  User = 'User',
}

export interface VerifyEmailOTP {
  email: string;
  provider: string;
  otpCode: string;
}

export interface UserDetails {
  id: number;
  role: string;
  username: string;
  firstName: string;
  middleName: string;
  lastName: string;
  fullName: string;
  gender: string;
  profileImageUrl: string;
  dateOfBirth: string;
  placeOfResidence: string;
  zipCode: string;
  street: string;
  email: string;
  city: string;
  state: string;
  country: string;
  signatureImage: string;
  phoneNumber: string;
  singupReasone: string;
  industry: string;
  notificationEnabled: boolean;
  isPassword: boolean;
  authType: 'Social' | 'Normal';
  kycCompleted: boolean;
  kycCompletedAt: string;
  kycStatus: string;
}

export interface ForgotPassword {
  email: string;
}

export interface ResetPassword {
  password: string;
  confirmPassword: string;
  email: string;
  token: string;
}

export interface DeactivateUserForm {
  email: string;
  password: string;
}

export interface ChangePasswordForm {
  email: string;
  newPassword: string;
  confirmPassword: string;
  currentPassword: string;
}

export interface SignupForm {
  email: string;
  role: number;
  cfTurnstileToken?: string;
}

export interface CreateUserPassword {
  email: string;
  password: string;
  confirmPassword: string;
}

export interface ChangePasswordForm {
  email: string;
  newPassword: string;
  confirmPassword: string;
  currentPassword: string;
}
export interface PrimeProperties {
  propertyList: Listing[];
}
export interface Listing {
  id: number;
  listingKey: string;
  city: string;
  latitude: number;
  longitude: number;
  listPrice: number;
  onMarketDate: string;
  originalListPrice: number;
  unparsedAddress: string;
  propertyType: string;
  propertySubType: string;
  photosCount: number;
  bedroomsTotal: number;
  country: string;
  postalCode: string;
  buildingName: string;
  bathroomsTotalInteger: number;
  parkingTotal: number;
  livingArea: number;
  yearBuilt: number;
  mlgCanView: boolean;
  media: ListingMedia[];
}
export interface ListingMedia {
  imageWidth: number;
  imageHeight: number;
  mediaURL: string;
}

export interface ISearchRequest {
  PageNumber: number;
  PageSize: number;
  SortColumn: string;
  SortOrder: string;
  Search: string;
}

export interface PropertySearch {
  pageNumber: number;
  pageSize: number;
  sortColumn: string;
  sortOrder: string;
  search: string;
  listPrice: number | null;
  bedroomsTotal: number | null;
  bathroomsTotalInteger: number | null;
  propertyType: string[] | null;
  parking: number | null;
  area: number | null;
  propertySubType: string[];
  city: string;
  address: string;
  minPrice: number | null;
  maxPrice: number | null;
  isSplit: boolean;
  southWestlat: number | null;
  southWestlng: number | null;
  northEastlat: number | null;
  northEastlng: number | null;
  polygon: DrawingPoint[] | null;
}

export type MapPropertySearchParams = Omit<PropertySearch, 'listPrice'>;

export interface PropertyMapItem {
  id: number;
  unparsedAddress: string;
  latitude: number;
  longitude: number;
  listPrice: number;
  media: MlsPropertyMediaDetails[];
  bathroomsTotalInteger: number;
  bedroomsTotal: number;
  listingKey: string;
  lotSizeSquareFeet: number;
  originatingSystemModificationTimestamp: string;
  propertyType: string;
  propertySubType: string;
  parking?: number;
  internetAddressDisplayYN: boolean;
  nwM_ShowMapLink: boolean;
}

export interface UserProperties {
  id: number;
  applicationUserId: number;
  assignedToUserId: number;
  title: string;
  category: string;
  propertyType: string;
  isCompanyListing: boolean;
  country: string;
  city: string;
  district: string;
  zipCode: string;
  streetAddress: string;
  totalArea: number;
  bedrooms: number;
  bathrooms: number;
  parkingSpots: number;
  amenities: string[];
  petsAllowed: boolean;
  description: string;
  price: number;
  priceTerm: string;
  mediaUrls: string[];
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  companyName: string;
  status: Status;
}

export interface Pagination {
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  totalRecords: number;
}

export enum Status {
  Submitted = 'Submitted',
  Draft = 'Draft',
  open = 'Open',
  resolved = 'Resolved',
  inProgress = 'InProgress',
  completed = 'Completed',
  sold = 'Marked As Sold',
  pending = 'Pending',
  rejected = 'Rejected',
  approved = 'Approved',
  published = 'Published',
}

export enum LeadTypes {
  schedule = 'Schedule',
}

export interface AddPropertyData {
  obj: PropertyForm;
}

export interface PropertyForm {
  id: string | null;
  applicationUserId: string | null;
  title: string;
  category: string;
  propertyType: string;
  propertySubType?: string;
  structureType?: string;
  isCertifiedAgent?: boolean;
  isCompanyListing?: boolean;
  country: string;
  city: string;
  district: string;
  zipCode?: string;
  streetAddress: string;
  totalArea: number;
  yearBuilt?: number;
  taxYear?: number;
  bedrooms?: string;
  bathrooms?: string;
  totalBedrooms?: number | string;
  mainLevelBedrooms?: number | string;
  upperLevelBedrooms?: number | string;
  lowerLevelBedrooms?: number | string;
  bathsTotal?: number | string;
  parkingSpots?: string | number;
  spaYN?: boolean;
  furnished?: string;
  heating?: string;
  hasCooling?: boolean;
  cooling?: string;
  levels?: string;
  basement?: string;
  waterSource?: string;
  waterfront?: boolean;
  electricOnProperty?: boolean;
  amenities: string[];
  petsAllowed: string[] | boolean;
  description: string;
  price: number;
  pricePerSqft?: number;
  mediaUrls: string[];
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  companyName?: string;
  status: 'Draft' | 'Submitted' | 'Pending' | 'Archived';
  images?: FileList[];
  latitude: number;
  longitude: number;
}

export interface Tickets {
  id: number;
  title: string;
  description: string;
  status: string;
  imageUrl: string;
  createdByUserName: string;
  createdByUserId: number;
  assignedToUserId: number;
  assignedToUserName: string;
  replies?: [];
}

export interface TicketReplyData {
  message: string;
  ticketId: number;
  image: File;
}

export interface MLSPropertiesData {
  list: MLSProperty[] | null;
  details: MlsPropertyDetails | null;
  query: PropertySearch | null;
  mlsMapProperties: PropertyMapItem[];
}

export interface googleMapLocation {
  lat: number;
  lng: number;
}

export interface MediaItem {
  order: number;
  mediaURL: string;
  propertyListingMlsId: number;
  imageWidth: number;
  imageHeight: number;
}

export interface MLSProperty {
  listingId: string;
  listingKey: string;
  propertyType: string;
  listPrice: number;
  city: string;
  bedroomsTotal: number;
  bathroomsTotalInteger: number;
  livingArea: number;
  unparsedAddress: string;
  media: MediaItem[];
  longitude: number;
  latitude: number;
  propertySubType: string;
  id: number;
  parking?: number;
  lotSizeSquareFeet: number;
  originatingSystemModificationTimestamp: string;
  nwM_ShowMapLink: boolean;
  internetAddressDisplayYN: boolean;
}

export interface MlsPropertyMediaDetails {
  mediaKey: string;
  mediaObjectID: string;
  mediaModificationTimestamp: string;
  resourceRecordKey: string;
  order: number;
  preferredPhotoYN: boolean;
  longDescription: string | null;
  imageWidth: number;
  imageHeight: number;
  imageSizeDescription: string;
  mediaURL: string;
}

export interface MlsPropertyDetails {
  odataContext: string;
  odataId: string;
  listingKeyNumeric: number;
  listingKey: string;
  listingId: string;
  originatingSystemModificationTimestamp: string;
  appliances: string[];
  nwM_AppliancesIncluded: string;
  nwM_Bathrooms: string;
  associationFee: number;
  associationYN: boolean;
  attachedGarageYN: boolean;
  basement: string[];
  bathroomsFull: number;
  bathroomsHalf: number;
  bathroomsThreeQuarter: number;
  bathroomsTotalInteger: number;
  nwM_BathsFullGarage: string;
  nwM_BathsFullLower: string;
  nwM_BathsFullMain: string;
  nwM_BathsFullUpper: string;
  nwM_BathsHalfGarage: string;
  nwM_BathsHalfLower: string;
  nwM_BathsHalfMain: string;
  nwM_BathsHalfUpper: string;
  nwM_BathsThreeQuarterGarage: string;
  nwM_BathsThreeQuarterLower: string;
  nwM_BathsThreeQuarterMain: string;
  nwM_BathsThreeQuarterUpper: string;
  nwM_BedroomsLower: string;
  bedroomsPossible: number;
  bedroomsTotal: number;
  buildingAreaTotal: number;
  buildingAreaUnits: string;
  buyerAgentFullName: string | null;
  buyerAgentKeyNumeric: number;
  buyerAgentKey: string | null;
  buyerAgentMlsId: string | null;
  buyerAgentOfficePhone: string | null;
  buyerFinancing: string | null;
  communityFeatures: string | null;
  cooling: string[];
  exteriorFeatures: string[];
  fireplaceFeatures: string | null;
  flooring: string[];
  foundationDetails: string | null;
  heating: string[];
  interiorFeatures: string[];
  levels: string[];
  listingTerms: string[];
  lotFeatures: string[];
  parkingFeatures: string[];
  possession: string[];
  powerProductionType: string[];
  propertyCondition: string[];
  vegetation: string[];
  view: string[];
  buyerOfficeKeyNumeric: number;
  buyerOfficeKey: string | null;
  buyerOfficeMlsId: string | null;
  buyerOfficeName: string | null;
  buyerOfficePhone: string | null;
  city: string;
  closeDate: string;
  closePrice: number;
  carportYN: boolean;
  coolingYN: boolean;
  coListAgentFullName: string | null;
  coListAgentKeyNumeric: number;
  coListAgentKey: string | null;
  coListAgentMlsId: string | null;
  coListOfficeKeyNumeric: number;
  coListOfficeKey: string | null;
  coListOfficeMlsId: string | null;
  coListOfficeName: string | null;
  coListOfficePhone: string | null;
  contractStatusChangeDate: string;
  country: string;
  countyOrParish: string;
  cumulativeDaysOnMarket: number;
  directions: string;
  electricOnPropertyYN: boolean;
  elementarySchool: string;
  fireplaceYN: boolean;
  furnished: string | null;
  garageYN: boolean;
  highSchool: string;
  highSchoolDistrict: string;
  internetAddressDisplayYN: boolean;
  internetAutomatedValuationDisplayYN: boolean;
  internetConsumerCommentYN: boolean;
  internetEntireListingDisplayYN: boolean;
  irrigationWaterRightsYN: boolean;
  latitude: number;
  listAgentFullName: string;
  listAgentKeyNumeric: number;
  listAgentKey: string;
  listAgentMlsId: string;
  listOfficeKeyNumeric: number;
  listOfficeKey: string;
  listOfficeMlsId: string;
  listOfficeName: string;
  listOfficePhone: string;
  listPrice: number;
  listingContractDate: string;
  livingArea: number;
  livingAreaUnits: string;
  longitude: number;
  lotSizeAcres: number;
  lotSizeDimensions: string;
  lotSizeUnits: string;
  mlsAreaMajor: string;
  middleOrJuniorSchool: string;
  mlsStatus: string;
  standardStatus: string;
  offMarketDate: string;
  onMarketDate: string;
  originalListPrice: number;
  parcelNumber: string;
  possibleUse: string | null;
  postalCode: string;
  propertySubType: string;
  propertyType: string;
  publicRemarks: string;
  purchaseContractDate: string;
  sewer: string[];
  sourceSystemName: string;
  specialListingConditions: string[];
  stateOrProvince: string;
  statusChangeTimestamp: string;
  streetName: string;
  streetNumber: string;
  streetNumberNumeric: number;
  streetSuffix: string;
  structureType: string[];
  subdivisionName: string;
  unparsedAddress: string;
  utilities: string | null;
  viewYN: boolean;
  waterSource: string[];
  waterfrontYN: boolean;
  zoningDescription: string | null;
  nwM_CalculatedSquareFootage: string;
  nwM_ElectricityStatus: string | null;
  elevationUnits: string;
  nwM_GasStatus: string | null;
  nwM_LotNumber: string | null;
  nwM_MLSLotSizeSource: string;
  nwM_Offers: string;
  nwM_PreliminaryTitleOrdered: boolean;
  nwM_SaleType: string;
  nwM_SellerDisclosure: string;
  nwM_SepticDesignInstalled: boolean;
  nwM_ShowMapLink: boolean;
  nwM_SketchSubmitted: boolean;
  nwM_SquareFootageFinished: string;
  nwM_SquareFootageUnfinished: string;
  nwM_StyleCode: string;
  nwM_RoadInformation: string | null;
  nwM_ZoningClass: string | null;
  nwM_ZoningJurisdiction: string;
  lotSizeSquareFeet: number;
  seniorCommunityYN: boolean;
  nwM_ThirdPartyApproval: string;
  originatingSystemName: string;
  modificationTimestamp: string;
  media: MlsPropertyMediaDetails[];
  photosCount: number;
  photosChangeTimestamp: string;
  mlgCanView: boolean;
  mlgCanUse: string[];
}

export interface ScheduleTour {
  userId: number;
  agentId?: number;
  propertyId: number;
  isScheduledTour: boolean;
  leadType: string;
  phoneNumber: string;
  status: string;
  scheduledTourDate: string;
}

export enum SchedulTourStatus {
  pending = 'Pending',
  completed = 'Completed',
  sold = 'Marked as Sold',
}

export interface UserScheduledTour {
  leadId: number;
  userId: number;
  userName: string;
  agentId: string;
  agentName: string | null;
  email: string;
  phoneNumber: string;
  propertyId: number;
  listingKey: string;
  unparsedAddress: string;
  isScheduledTour: boolean;
  leadType: 'Schedule';
  scheduledTourDate: string;
}

export interface UserBlogs {
  title: string;
  content: string;
  publishedDate: string;
  coverImageUrl: string;
  tags: string;
  id: number;
}

export interface AgentAppointments extends UserProperties {}

export interface ContactUsData {
  name: string;
  email: string;
  phoneNumber: string;
  address: string;
  messages: string;
}

export interface MessagesRequest {
  senderId: string;
  recieverId: string;
}

export interface SendMessageRequest {
  id?: number;
  senderId: number;
  receiverId: number;
  messageText: string;
  mediaUrl?: string;
  file?: string;
  contentType?: string;
  timestamp?: string;
  isRead?: boolean;
}

export interface MessagesList {
  imageUrl: string;
  userId: number;
  userName: string;
}

export interface SocialAuthProvidersFrom {
  token: string;
  provider: string;
}

export interface AgentPortfolio {
  salesVolume: number;
  totalDeals: number;
  highestSalePrice: number;
  bio: string;
  homesClosed: number;
  homesClosedInLocation: string;
  teamMembers: TeamMembers[];
  testimonials: Testimonials[];
  title: string;
}

export interface TeamMembers {
  name: string;
  photoUrl: string;
  designation: string;
}
export interface Testimonials {
  name: string;
  country: string;
  review: string;
  createdOn?: string;
}

export interface allRegisteredAgents {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  profileImageUrl: string;
  roles: string;
  isActive: boolean;
  deleted: boolean;
  applicationId: number;
  jobTitle: string;
  company: string;
  licenseNumber: string;
  submittedOn: string;
  applicationStatus: string;
  role: 'Agent';
  address: 'Moatabar Boys Hostel (MBH), CIT Colony 17 Warsak Road opp. CDA Model School near Potohar metro Station I 9/4, Pakistan';
  city: 'Islamabad';
  state: 'punjab';
  country: 'Pakistan';
  postalCode: '000';
  identityDocumentUrl: 'https://aris360storage.blob.core.windows.net/media-files/ImageFolder/AgentDocsedd3617e-9cbd-4f78-89e4-0c6b463f5f61-image.jpg';
  heardAboutUs: null;
  workFrom: '2025-05-01T00:00:00';
  workTo: null;
  isCurrentlyEmployed: true;
  resumeUrl: 'https://aris360storage.blob.core.windows.net/media-files/ImageFolder/AgentResumes126b2981-9f13-4add-8c85-80b17df1fc0a-test file.pdf';
  skills: 'angular';
  websiteUrl: null;
  linkedInUrl: null;
  language: null;
  hasActiveLicense: true;
  dealsClosed: 10;
  pastActions: true;
  disciplined: true;
  underInvestigation: true;
  hasRestrictions: true;
  isUnrestricted: true;
  isMemberNAREB: true;
  isMemberNAHREP: false;
  isMemberAREA: false;
  isMemberOther: false;
  otherAssociations: null;
  veteranStatus: 'disabledVeteran';
  gender: 'male';
  ethnicity: 'New York, NY';
  rejectionReason: null;
  reviewedOn: '2025-05-22T20:26:43.6924249';
  testimonials: Testimonials[];
}

export interface AutocompletePrediction {
  displayName: string;
  description: string;
}

export enum CategoryType {
  PropertType = 'PropertyType',
  PropertSubType = 'PropertySubType',
}

export interface youtube {
  kind: string;
  etag: string;
  id: {
    kind: string;
    videoId: string;
  };
  snippet: {
    publishedAt: string;
    channelId: string;
    title: string;
    description: string;
    thumbnails: {
      default: {
        url: string;
        width: number;
        height: number;
      };
      medium: {
        url: string;
        width: number;
        height: number;
      };
      high: {
        url: string;
        width: number;
        height: number;
      };
    };
    channelTitle: string;
    liveBroadcastContent: string;
    publishTime: string;
  };
}

export interface tweets {
  edit_history_tweet_ids: string[];
  created_at: string;
  text: string;
  id: string;
}

export interface NewsletterSubscribe {
  email: string;
}

export interface ContactAgent {
  agentEmail: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  interest: string;
  message: string;
}

export interface LeadDetails {
  leadId: number;
  userId: number;
  userName: string;
  agentId: number;
  agentName: string | null;
  email: string;
  phoneNumber: string;
  propertyId: number;
  listingKey: string;
  unparsedAddress: string;
  isScheduledTour: boolean;
  leadType: 'Schedule' | string;
  status: 'Pending' | string;
  image: string;
  scheduledTourDate: string;
}

export interface ContactQueries {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  message: string;
  submittedOn: string;
  interest: string;
}

export interface PayrollSubmissions {
  id?: number;
  propertyAddress: string;
  mlsNumber: string;
  dateOfClosing: string;
  transactionType: string;
  salesPrice: number;
  commissionPercentage: number;
  agentCommissionPercentage: number;
  escrowCompanyName: string;
  escrowOfficerName: string;
  escrowCompanyPhone: string;
  escrowCompanyEmail: string;
  escrowFileNumber: string;
  specialPaymentInstructions: string;
  otherAgents: string;
  referralsPaid: boolean;
  referralAmount: number;
  transactionNotes: string;
  saveAsDraft: boolean;
  attachments?: PayrollAttachment[];
  status: PayrollStatus;
  rejectedReason?: string;
}

export interface PayrollDocuments {
  submissionId: number;
  file: File;
  documentType: string;
}

export enum PayrollStatus {
  Draft = 0,
  Submitted = 1,
  UnderReview = 2,
  Approved = 3,
  Paid = 4,
  Rejected = 5,
}

export interface PayrollAttachment {
  blobUrl: string;
  documentType:
    | 'Form40'
    | 'FinalSettlement'
    | 'DisbursementLedger'
    | 'PurchaseSaleAgreement';
  contentType: string;
  fileName: string;
  id: number;
  sizeBytes: number;
}
export interface OffMarketPropertyImage {
  blobUrl: string;
  id: number;
  contentType: string;
  fileName: string;
  sizeBytes: number;
  order: number;
}
export interface OffMarketProperty {
  title: string;
  category: string;
  propertyType: string;
  isCompanyListing: boolean;
  country: string;
  city: string;
  district: string;
  zipCode: string;
  streetAddress: string;
  totalArea: number;
  bedrooms: number;
  bathrooms: number;
  parkingSpots: number;
  amenities: string[] | string;
  petsAllowed: boolean;
  description: string;
  price: number;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  companyName: string;
  status: 'Draft' | 'Submitted' | 'Pending' | 'Archived';
  latitude: number;
  longitude: number;
  applicationUserId: number;
  id: number;
  images: OffMarketPropertyImage[];
}

export type CreateOffMarketProperty = Omit<
  OffMarketProperty,
  'id' | 'applicationUserId' | 'images'
>;

export interface RentalListing {
  id: number;
  applicationUserId: number;
  country?: string;
  streetAddress: string;
  city: string;
  state: string;
  zipPostalCode: string;
  propertySubType?: string;
  numberOfBedrooms: string;
  numberOfBathrooms: number;
  squareFootage: number;
  headline: string;
  description: string;
  monthlyRent: number;
  securityDeposit: number;
  leaseType: string;
  durationMonths: number;
  availableDate: string;
  petsAllowed: boolean;
  smokingAllowed: boolean;
  latitude?: number;
  longitude?: number;
  amenities: string[];
  utilitiesIncluded: string[];
  acceptApplicationsThroughAris360: boolean;
  applicationFee: number;
  fullName: string;
  emailAddress: string;
  phoneNumber: string;
  mediaUrls: string[];
  status: string;
  kycCompleted: boolean;
  submittedAtUtc: string;
  approvedAtUtc: string | null;
  rejectedAtUtc: string | null;
  rejectionReason: string | null;
  approvedByAdminId: number;
  propertyType?: string | null;
  message?: string | null;
}

export interface RentalListingsRequest {
  PageNumber: number;
  PageSize: number;
  SortColumn: string;
  SortOrder: string;
  Search: string;
}

export interface RentalSearchRequest {
  pageNumber: number;
  pageSize: number;
  sortColumn: string;
  sortOrder: string;
  search: string;
  minPrice?: number;
  maxPrice?: number;
  bedroomsTotal?: number | string;
  bathroomsTotalInteger?: number | string;
  parking?: number;
  area?: number;
  propertyType?: string[];
  propertySubType?: string[];
  city?: string;
  address?: string;
  isSplit?: boolean;
  isPrime?: boolean;
  southWestlat?: number;
  southWestlng?: number;
  northEastlat?: number;
  northEastlng?: number;
  polygon?: Array<{ lat: number; lng: number }> | null;
}

export interface RentalMapMarkerItem {
  id: number;
  headline: string;
  monthlyRent: number;
  latitude: number;
  longitude: number;
  numberOfBedrooms: string;
  numberOfBathrooms: number;
  city: string;
  squareFootage?: number;
  mediaUrls: string[];
  propertyType?: string;
  propertySubType?: string;
  parking?: number;
  address?: string;
  status: string;
}
export interface MarketedPropertyListing {
  id: number;
  applicationUserId: number;
  title: string;
  category: string;
  propertyType: string;
  propertySubType: string;
  structureType: string;
  country: string;
  city: string;
  district: string;
  zipCode: string;
  streetAddress: string;
  totalArea: number;
  yearBuilt: number;
  taxYear: number;
  parkingSpots: number;
  spaYN: boolean;
  furnished: string;
  latitude: number;
  longitude: number;
  amenities: string[];
  petsAllowed: boolean;
  bathsTotal: number;
  totalBedrooms: number;
  mainLevelBedrooms: number;
  upperLevelBedrooms: number;
  lowerLevelBedrooms: number;
  heating: string;
  hasCooling: boolean;
  cooling: string;
  levels: string;
  basement: string;
  waterSource: string;
  waterfront: boolean;
  electricOnProperty: boolean;
  description: string;
  price: number;
  pricePerSqft: number;
  mediaUrls: string[];
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  companyName: string;
  licenseNumber: string;
  agentMlsId: string;
  managingBroker?: string;
  status: string;
  submittedAtUtc: string;
  approvedAtUtc: string;
  rejectedAtUtc: string;
  rejectionReason: string;
  approvedByAdminId: number;
  listingFee: number;
  listingFeePaymentRequired: boolean;
  listingFeePaymentDueDate: string;
  isPaymentDone?: boolean;
}

export type ListingPaymentMethod = 'stripe' | 'paypal';

export interface ListingPaymentInitiateRequest {
  marketedPropertyId: number;
  paymentMethod: ListingPaymentMethod;
  customerEmail: string;
  returnUrl: string;
}

export interface ListingPaymentInitiateResult {
  redirectUrl?: string;
  checkoutUrl?: string;
  approvalUrl?: string;
  url?: string;
  sessionId?: string;
  id?: string;
  stripeSessionId?: string;
  stripeSessionUrl?: string;
  payPalApprovalUrl?: string;
}

export interface ListingPaymentInitiateResponse extends BaseInterface {
  result: ListingPaymentInitiateResult;
}

export interface PayPalExecutePaymentRequest {
  paymentId: string;
  payerId: string;
}

export interface KycIdentitySessionResult {
  clientSecret?: string;
}

export interface KycIdentitySessionResponse extends BaseInterface {
  result: KycIdentitySessionResult;
}
